import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useParams,
  useNavigate
} from 'react-router-dom';
import axios from 'axios';
import "./App.css"
import { BACKEND_URL } from './constants';

const MAX_FILE_SIZE_MB = 100_000;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type Option = { label: string; value: string };
type MediaKind = 'image' | 'video' | 'audio' | 'pdf';
type OverlayState = { kind: 'image' | 'video' | 'pdf'; src: string; name: string };

const IMAGE_EXTS = new Set([
  'apng', 'avif', 'bmp', 'gif', 'ico', 'jfif', 'jpeg', 'jpg', 'pjp', 'pjpeg', 'png', 'svg', 'tif', 'tiff', 'webp',
]);
const VIDEO_EXTS = new Set([
  '3gp', 'avi', 'm4v', 'mkv', 'mov', 'mp4', 'mpeg', 'mpg', 'ogv', 'webm',
]);
const AUDIO_EXTS = new Set([
  'aac', 'aif', 'aiff', 'flac', 'm4a', 'mp3', 'oga', 'ogg', 'opus', 'wav', 'weba',
]);

function fileExt(name: string): string {
  const i = name.lastIndexOf('.');
  if (i < 0 || i === name.length - 1) return '';
  return name.slice(i + 1).toLowerCase();
}

function mediaKind(fileName: string): MediaKind | null {
  const ext = fileExt(fileName);
  if (IMAGE_EXTS.has(ext)) return 'image';
  if (VIDEO_EXTS.has(ext)) return 'video';
  if (AUDIO_EXTS.has(ext)) return 'audio';
  if (ext === 'pdf') return 'pdf';
  return null;
}

function fileKeyFromStoredUrl(fileUrl: string): string {
  const url = new URL(fileUrl);
  const rawPath = url.pathname.startsWith('/') ? url.pathname.slice(1) : url.pathname;
  const parts = rawPath.split('/');
  return parts.length > 1 ? parts.slice(1).join('/') : rawPath;
}

function fileApiUrl(pasteId: string, key: string, preview: boolean): string {
  const qp = new URLSearchParams({ key });
  if (preview) qp.set('preview', '1');
  return `${BACKEND_URL}/paste/${pasteId}/file?${qp.toString()}`;
}

const TTL_OPTIONS: Option[] = [
  { label: '5 minutes', value: String(60 * 5) },
  { label: '1 hour', value: String(60 * 60) },
  { label: '1 day', value: String(60 * 60 * 24) },
  { label: '7 days', value: String(60 * 60 * 24 * 7) },
  { label: '30 days', value: String(60 * 60 * 24 * 30) },
  { label: '1 year', value: String(60 * 60 * 24 * 365) },
  { label: 'Forever', value: 'undefined' },
];

const Dropdown: React.FC<{
  value: string;
  onChange: (newValue: string) => void;
  options: Option[];
}> = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find(o => o.value === value) ?? options[0];

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div className="dropdown" ref={rootRef}>
      <button type="button" className="dropdownButton" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open}>
        <span>{selected.label}</span>
        <svg className={`chevron ${open ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 10l5 5 5-5z"/></svg>
      </button>
      {open && (
        <div className="dropdownList" role="listbox">
          {options.map(opt => (
            <button
              type="button"
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`dropdownItem ${opt.value === value ? 'selected' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CreatePaste: React.FC = () => {
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<FileList | null>(null);
  const [ttlSecondStr, setTtlSecondStr] = useState("300");
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordHash, setPasswordHash] = useState('');

  useEffect(() => {
    const compute = async () => {
      if (!password) { setPasswordHash(''); return; }
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const digest = await crypto.subtle.digest('SHA-256', data);
      const bytes = Array.from(new Uint8Array(digest));
      const hex = bytes.map(b => b.toString(16).padStart(2, '0')).join('');
      setPasswordHash(hex);
    };
    void compute();
  }, [password]);
  const [creating, setCreating] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [uploadedBytes, setUploadedBytes] = useState<number>(0)
  const [totalBytes, setTotalBytes] = useState<number>(0)
  const [speedBps, setSpeedBps] = useState<number>(0)
  const [pendingPasteId, setPendingPasteId] = useState<string | null>(null)

  const formatBytes = (num: number): string => {
    if (!isFinite(num)) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let n = num;
    let i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
  };
  const navigate = useNavigate();

  const createPaste = async () => {
    if (content.length <= 0 && (files?.length ?? 0) <= 0) {
      setError("Your paste must not be empty!")
      return
    }

    const formData = new FormData();
    formData.append("content", content);
    const ttlSecond = ttlSecondStr === 'undefined' ? undefined : parseInt(ttlSecondStr);

    const fileArray: File[] = []
    if (files) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].size > MAX_FILE_SIZE_BYTES) {
          setError(`File "${files[i].name}" exceeds ${MAX_FILE_SIZE_MB} MB!`)
          return;
        }
      }
      for (let i = 0; i < files.length; i++) {
        fileArray.push(files[i])
      }
    }

    const filesRequests = fileArray.map((file, i) => ({
      clientId: `${i}`,
      originalName: file.name,
      size: file.size,
    }))

    try {
      setCreating(true)
      const total = fileArray.reduce((sum, f) => sum + f.size, 0)
      setTotalBytes(total)
      setUploadedBytes(0)
      setSpeedBps(0)
      setUploadProgress(fileArray.length > 0 ? 0 : null)
      const { data } = await axios.post(`${BACKEND_URL}/paste`, {
        content,
        ttlSecond,
        files: filesRequests,
        // Only send passwordHash, never the plain password
        passwordHash: passwordHash || undefined,
      });
      if (data?.id) {
        setPendingPasteId(String(data.id));
      }
      let uploadedBytesAcc = 0;
      let lastTick = Date.now();
      let lastUploaded = 0;
      for (let i = 0; i < data.fileUploadPresigned.length; i++) {
        const presignedData = data.fileUploadPresigned[i].data;
        const formData = new FormData();
        Object.entries(presignedData.fields).forEach(([k, v]) => {
          formData.append(k, v as any);
        });
        formData.append("file", fileArray[i]); // must be the last one

        // upload file[i] to S3 using presigned URL
        const baseUploaded = uploadedBytesAcc;
        await axios.post(presignedData.url, formData, {
          // eslint-disable-next-line no-loop-func
          onUploadProgress: (e) => {
            if (total > 0 && e.loaded != null) {
              const now = Date.now();
              const deltaT = Math.max(1, now - lastTick) / 1000; // seconds
              const uploadedNow = baseUploaded + e.loaded;
              const deltaBytes = Math.max(0, uploadedNow - lastUploaded);
              const bps = deltaBytes / deltaT;
              lastTick = now; lastUploaded = uploadedNow;

              const overallPct = Math.min(100, Math.round((uploadedNow / total) * 100));
              setUploadProgress(overallPct);
              setUploadedBytes(uploadedNow);
              setSpeedBps(bps);
            }
          },
        });
        uploadedBytesAcc += fileArray[i].size;
        if (total > 0) {
          const overall = Math.min(100, Math.round((uploadedBytesAcc / total) * 100));
          setUploadProgress(overall);
          setUploadedBytes(uploadedBytesAcc);
        }
      }
      navigate("/" + data.id);
    } catch (err: any) {
      console.error('Error creating paste:', error);
      setError(err.response?.status + " " + JSON.stringify(err.response?.data))
      setCreating(false)
      setUploadProgress(null)
      setPendingPasteId(null)
    }
  };

  return (
    <div className="container">
      <h1>
        <Link to="/">Create Personal Paste</Link>
      </h1>

      <div className="card">
        <div>
          <textarea
            placeholder="Enter your paste content"
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        <div className="controls">
          <input type="file" multiple onChange={(e) => setFiles(e.target.files)} />
          <input
            placeholder="Password (optional)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            type="password"
          />
          <Dropdown value={ttlSecondStr} onChange={setTtlSecondStr} options={TTL_OPTIONS} />
          <button disabled={creating} onClick={createPaste}> {creating ? "Creating..." : "Create"}</button>
        </div>
        {creating && pendingPasteId && (
          <div className="copyRow">
            {(() => {
              const origin = typeof window !== 'undefined' ? window.location.origin : '';
              const domainPart = typeof window !== 'undefined' ? `${window.location.host}/` : '';
              const shareUrl = origin && pendingPasteId ? `${origin}/${pendingPasteId}` : '';
              return (
                <>
                  <button onClick={() => {
                    if (shareUrl) navigator.clipboard.writeText(shareUrl);
                  }} className="copyLinkButton">Copy link</button>
                  <div className="linkPreview" title={shareUrl}>
                    <span className="linkText">
                      <span className="linkDomain">{domainPart}</span>
                      <span className="linkPath">{pendingPasteId}</span>
                    </span>
                  </div>
                </>
              )
            })()}
          </div>
        )}

        {creating && uploadProgress !== null && (
          <div className="progressWrap">
            <div className="progressTrack">
              <div className="progressFill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {uploadProgress}% • {formatBytes(uploadedBytes)} / {formatBytes(totalBytes)} • {formatBytes(speedBps)}/s
            </div>
          </div>
        )}
        {error.length > 0 && <p className="muted" style={{ color: "#ff6b6b" }}>{error}</p>}
      </div>
    </div>
  );
};

const VideoThumb: React.FC<{ src: string }> = ({ src }) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  return (
    <video
      ref={ref}
      className="fileThumb"
      src={src}
      muted
      playsInline
      preload="metadata"
      onLoadedMetadata={() => {
        const v = ref.current;
        if (v && Number.isFinite(v.duration) && v.duration > 0.1) {
          v.currentTime = Math.min(0.5, v.duration * 0.1);
        }
      }}
    />
  );
};

const AudioPlayButton: React.FC<{ src: string; name: string }> = ({ src, name }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  return (
    <>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
      <button
        type="button"
        className="audioPlay"
        onClick={() => {
          const a = audioRef.current;
          if (!a) return;
          if (a.paused) void a.play();
          else a.pause();
        }}
        aria-label={playing ? `Pause ${name}` : `Play ${name}`}
      >
        {playing ? (
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>
        )}
      </button>
    </>
  );
};

const FileAttachments: React.FC<{
  pasteId: string;
  files: { fileName: string; url: string }[];
  passwordRequired: boolean;
  passwordHash: string;
  onError: (msg: string) => void;
}> = ({ pasteId, files, passwordRequired, passwordHash, onError }) => {
  const [overlay, setOverlay] = useState<OverlayState | null>(null);
  const [mediaSrc, setMediaSrc] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const next: Record<string, string> = {};
      await Promise.all(files.map(async (file) => {
        if (!mediaKind(file.fileName)) return;
        const key = fileKeyFromStoredUrl(file.url);
        const apiUrl = fileApiUrl(pasteId, key, true);
        try {
          const headers: Record<string, string> = {};
          if (passwordRequired && passwordHash) headers.Authorization = `Basic ${passwordHash}`;
          const url = passwordRequired && passwordHash ? apiUrl : `${apiUrl}&json=1`;
          const resp = await fetch(url, { headers });
          if (!resp.ok) return;
          const j = await resp.json() as { url?: string };
          if (j?.url) next[key] = j.url;
        } catch {
          // keep download-only
        }
      }));
      if (!cancelled) setMediaSrc(next);
    };
    void resolve();
    return () => { cancelled = true; };
  }, [files, pasteId, passwordRequired, passwordHash]);

  useEffect(() => {
    if (!overlay) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOverlay(null);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [overlay]);

  const downloadFile = (downloadUrl: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!(passwordRequired && passwordHash)) return;
    e.preventDefault();
    fetch(downloadUrl, { headers: { Authorization: `Basic ${passwordHash}` } })
      .then(async (resp) => {
        if (resp.status === 401) onError('Download error, password required');
        else if (resp.ok) {
          const j = await resp.json() as { url?: string };
          if (j?.url) window.location.href = j.url;
        } else onError(`${resp.status} download error`);
      })
      .catch(() => onError('Download error'));
  };

  return (
    <div className="fileList">
      <span className="muted">File(s):</span>
      {files.map((file) => {
        const key = fileKeyFromStoredUrl(file.url);
        const downloadUrl = fileApiUrl(pasteId, key, false);
        const kind = mediaKind(file.fileName);
        const src = mediaSrc[key];
        return (
          <div className={kind ? 'fileItem' : 'tag'} key={file.url}>
            {kind === 'image' && src && (
              <button
                type="button"
                className="fileThumbButton"
                style={{ backgroundImage: `url(${JSON.stringify(src)})` }}
                onClick={() => setOverlay({ kind: 'image', src, name: file.fileName })}
                aria-label={`Preview ${file.fileName}`}
              />
            )}
            {kind === 'video' && src && (
              <button
                type="button"
                className="fileThumbButton"
                onClick={() => setOverlay({ kind: 'video', src, name: file.fileName })}
                aria-label={`Preview ${file.fileName}`}
              >
                <VideoThumb src={src} />
                <span className="playBadge" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>
                </span>
              </button>
            )}
            {kind === 'audio' && src && (
              <AudioPlayButton src={src} name={file.fileName} />
            )}
            {kind === 'pdf' && src && (
              <button
                type="button"
                className="fileThumbButton pdfThumb"
                onClick={() => setOverlay({ kind: 'pdf', src, name: file.fileName })}
                aria-label={`Preview ${file.fileName}`}
              >
                <span className="pdfThumbLabel">PDF</span>
              </button>
            )}
            <a href={downloadUrl} target="_blank" rel="noreferrer" onClick={(e) => downloadFile(downloadUrl, e)}>
              {file.fileName}
            </a>
          </div>
        );
      })}
      {overlay && (
        <div
          className="mediaOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={overlay.name}
          onClick={() => setOverlay(null)}
        >
          <button type="button" className="mediaOverlayClose" onClick={() => setOverlay(null)} aria-label="Close preview">×</button>
          {overlay.kind === 'image' ? (
            <img src={overlay.src} alt={overlay.name} onClick={(e) => e.stopPropagation()} />
          ) : overlay.kind === 'pdf' ? (
            <iframe
              className="pdfFrame"
              title={overlay.name}
              src={overlay.src}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <video src={overlay.src} controls autoPlay onClick={(e) => e.stopPropagation()} />
          )}
        </div>
      )}
    </div>
  );
};

const ReadPaste: React.FC = () => {
  const [paste, setPaste] = useState<{
    content: string,
    uploadedFiles: { fileName: string, url: string }[]
    ttl?: number
  } | null>(null);
  const { id } = useParams<{ id: string }>();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [remainingTime, setRemainingTime] = useState('');
  const [password, setPassword] = useState('');
  const [passwordHash, setPasswordHash] = useState('');
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordUnlocked, setPasswordUnlocked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();


  const fetchPaste = async () => {
    try {
      const headers: Record<string, string> = {};
      if (passwordHash) headers['Authorization'] = `Basic ${passwordHash}`;
      const { data } = await axios.get(`${BACKEND_URL}/paste/${id}`, { headers });
      setPaste(data);
      setError("")
      if (passwordRequired) {
        setPasswordUnlocked(true);
      }
    } catch (err: any) {
      console.error('Error fetching paste:', error);
      if (err?.response?.status === 401) {
        setPasswordRequired(true);
        setError('');
        setNotice('This paste is password-protected. Please enter the password.');
      } else if (err?.response?.status === 404) {
        setError('Paste not found');
      } else {
        setError(err.response?.status + " " + JSON.stringify(err.response?.data))
      }
    }
  };

  useEffect(() => {
    const compute = async () => {
      if (!password) { setPasswordHash(''); return; }
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const digest = await crypto.subtle.digest('SHA-256', data);
      const bytes = Array.from(new Uint8Array(digest));
      const hex = bytes.map(b => b.toString(16).padStart(2, '0')).join('');
      setPasswordHash(hex);
    };
    void compute();
  }, [password]);

  const updateRemainingTime = useCallback(() => {
    if (paste && paste?.ttl === undefined) {
      setRemainingTime("This paste will never expire (forever)");
      return;
    }

    if (paste?.ttl) {
      const now = Date.now() / 1000;
      const diff = paste.ttl - now;
      const date = new Date(paste.ttl * 1000);
      const absoluteTime = date.toLocaleString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'short',
      });

      if (diff > 0) {
        const months = Math.floor(diff / (3600 * 24 * 30));
        const days = Math.floor((diff % (3600 * 24 * 30)) / (3600 * 24));
        const hours = Math.floor((diff % (3600 * 24)) / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = Math.floor(diff % 60);

        let remainingTimeString = `Will expire on ${absoluteTime} (in `;
        if (months > 0) remainingTimeString += `${months} months `;
        if (days > 0) remainingTimeString += `${days} days `;
        if (hours > 0) remainingTimeString += `${hours} hours `;
        if (minutes > 0) remainingTimeString += `${minutes} minutes `;
        if (seconds > 0) remainingTimeString += `${seconds} seconds`;
        remainingTimeString = remainingTimeString.trim() + ")";

        setRemainingTime(remainingTimeString);
      } else {
        setRemainingTime(`Expired at ${absoluteTime}`);
      }
    }
  }, [paste])

  useEffect(() => {
    fetchPaste();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    updateRemainingTime()
    const interval = setInterval(updateRemainingTime, 1000);
    return () => clearInterval(interval);
  }, [updateRemainingTime]);

  const [copyText, setCopyText] = useState('Copy link');
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const urlObj = shareUrl ? new URL(shareUrl) : null;
  const domainPart = urlObj ? `${urlObj.host}/` : '';
  const pathPart = urlObj ? `${urlObj.pathname.replace(/^\//, '')}${urlObj.search}${urlObj.hash}` : '';

  return (
    <div className="container">
      <h1>
        <Link to="/">Create Personal Paste</Link>
      </h1>

      {paste && (
        <div className="copyRow">
          <button onClick={() => {
            navigator.clipboard.writeText(shareUrl);
            setCopyText('Copied!');
            setTimeout(() => {
              setCopyText('Copy link');
            }, 2000);
          }} className="copyLinkButton">{copyText}</button>
          <div className="linkPreview" title={shareUrl}>
            <span className="linkText">
              <span className="linkDomain">{domainPart}</span>
              <span className="linkPath">{pathPart || '/'}</span>
            </span>
          </div>
        </div>
      )}

      <p className="muted">{remainingTime}</p>

      {passwordRequired && !paste && !passwordUnlocked && (
        <div className="controls">
          <input
            type="password"
            className="input"
            placeholder="Password required"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button onClick={fetchPaste}>Unlock</button>
        </div>
      )}

      {paste?.uploadedFiles?.length && id
        ? <FileAttachments
          pasteId={id}
          files={paste.uploadedFiles}
          passwordRequired={passwordRequired}
          passwordHash={passwordHash}
          onError={setError}
        />
        : null}

      {!paste && (
        error.length === 0
          ? (passwordRequired ? <div className="alert info">{notice}</div> : <p>Loading...</p>)
          : <div className="alert error">{error}</div>
      )}

      {paste && <pre className="content">{paste.content}</pre>}

      {paste && passwordRequired && passwordUnlocked && (
        <div className="controls">
          <button
            disabled={deleting}
            onClick={async () => {
              if (!id || !passwordHash) return;
              if (!window.confirm('Delete this paste and its files? This cannot be undone.')) return;
              try {
                setDeleting(true);
                await axios.delete(`${BACKEND_URL}/paste/${id}`, {
                  headers: { Authorization: `Basic ${passwordHash}` },
                });
                navigate('/');
              } catch (err: unknown) {
                const ax = err as { response?: { status?: number; data?: unknown } };
                setError(`${ax.response?.status ?? ''} ${JSON.stringify(ax.response?.data)}`);
                setDeleting(false);
              }
            }}
          >{deleting ? 'Deleting...' : 'Delete paste'}</button>
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <div>

        <Routes>
          <Route path="/:id" Component={ReadPaste} />
          <Route path="/" Component={CreatePaste} />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
